import { ApplicationState } from '@/store';
import { Link as ChakraLink } from '@chakra-ui/react';
import { DefaultDashboardTabIds } from 'data';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link as ReactRouterLink, useNavigate, useLocation } from 'react-router-dom';
import { UserRole } from '@/store/auth/types';
import { TableRow } from '../../types';
import { getIsPartner } from '@/store/selectors';

interface IRowEventsHandler {
  children?: React.ReactNode;
  rowProps?: TableRow;
  index?: number;
  isRowSelected?: boolean;
  gridColumnsWidth?: string;
}

export const RowEventsHandler = (props: IRowEventsHandler) => {
  return <OfferDetailsRow {...props} />;
};

export const OfferDetailsRow = (props: IRowEventsHandler) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { children, rowProps, index, isRowSelected, gridColumnsWidth } = props;
  const { uni, applicationId, userId, offerId } = rowProps;

  const activeTab = useSelector(({ offersDashboard }: ApplicationState) => offersDashboard.currentTab);
  const isLeadStage = activeTab === DefaultDashboardTabIds.LEADS;
  const isAnyApplyStage = [
    DefaultDashboardTabIds.APPLIED,
    DefaultDashboardTabIds.UNCONDITIONAL,
    DefaultDashboardTabIds.CONDITIONAL,
  ].includes(activeTab as DefaultDashboardTabIds);

  const partialApplyJourney = useSelector(({ config }: ApplicationState) => config.partialApplyJourney);
  const isAgent = useSelector(
    ({ auth }: ApplicationState) => auth.role === UserRole.Agent || auth.role === UserRole.AgencyAdmin,
  );
  const isPartner = useSelector(getIsPartner);

  let route = `/application/${uni}/${userId}/${applicationId}/summary`;
  if (isAgent || isPartner) {
    if (isLeadStage) {
      route = `/apply/application/${uni}/${userId}`;
    } else if (isAnyApplyStage) {
      route = `/apply/application/${uni}/${userId}/applications/${applicationId}`;
    } else if (partialApplyJourney) {
      route = `/apply/application/${uni}/${userId}/applications/${applicationId}`;
    } else {
      route = `/apply/application/${uni}/${userId}/offers/${offerId}`;
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
    event.preventDefault();
    event.stopPropagation();
    if (event.isDefaultPrevented() && event.isPropagationStopped()) {
      sessionStorage.setItem('cas-staff-uni-backUrl', `admissions/${location.search}`);
      if (isAgent || isPartner) {
        window.location.href = route;
      } else {
        navigate(route);
      }
    }
  };

  return (
    <ChakraLink
      as={ReactRouterLink}
      to={route}
      onClick={handleClick}
      role="row"
      className={isRowSelected ? 'grid-Row selected' : 'grid-Row'}
      display="grid"
      height={24}
      gridTemplateColumns={gridColumnsWidth}
      bgColor="zebra-bg"
      _even={{ bgColor: 'chakra-subtle-bg' }}
      cursor="pointer"
      tabIndex={0}
      aria-rowindex={index + 2}
      aria-selected={isRowSelected}
      sx={{
        contain: 'layout style paint',
        '.namesContainer': {
          color: 'link-color',
        },
        _visited: {
          '.namesContainer': {
            color: 'link-visited-color',
          },
        },
        _hover: {
          bgColor: 'hover-bg',
          '.namesContainer': {
            color: 'link-hover-color',
          },
        },
        _focusVisible: {
          bgColor: 'hover-bg',
          '.grid-Cell:first-of-type': {
            bg: 'linear-gradient(to right, var(--ds-colors-link-color) 0, var(--ds-colors-link-color) 4px, transparent 4px, transparent 100%)',
            bgColor: 'inherit',
          },
        },
        _active: {
          bgColor: 'selected-bg',
          '.namesContainer': {
            color: 'link-active-color',
          },
        },
        '&.selected': {
          bgColor: 'selected-bg',
          '.namesContainer': {
            color: 'link-active-color',
          },
        },
      }}
    >
      {children}
    </ChakraLink>
  );
};
